import { queryOptions, useQuery } from '@tanstack/react-query';

import { makeIsEnabled } from '@cast/utils';

import { apiClient } from '@cast/infrastructure-api';

const COMMITMENTS_LIST_QUERY_KEY = 'commitments-list';

const getCommitmentQueryKey = ({
  commitmentId,
  includeUsage,
  withInstanceTypes,
}: {
  commitmentId?: string;
  includeUsage: boolean;
  withInstanceTypes: boolean;
}) =>
  [
    COMMITMENTS_LIST_QUERY_KEY,
    commitmentId,
    includeUsage,
    withInstanceTypes,
  ] as const;

const getCommitmentQueryOptions = ({
  commitmentId,
  includeUsage,
  withInstanceTypes,
  enabled,
}: {
  commitmentId?: string;
  includeUsage: boolean;
  withInstanceTypes: boolean;
  enabled: boolean;
}) =>
  queryOptions({
    queryKey: getCommitmentQueryKey({
      commitmentId,
      includeUsage,
      withInstanceTypes,
    }),
    queryFn: async () => {
      const { data } = await apiClient.commitments.getCommitment({
        commitmentId: commitmentId!,
        includeUsage,
        includeUsagePerClusters: includeUsage,
        includeUsagePerInstanceTypes: withInstanceTypes,
      });
      return data;
    },
    enabled: makeIsEnabled(enabled, !!commitmentId),
  });

type UseCommitmentQueryArgs = {
  commitmentId?: string;
  includeUsage?: boolean;
  withInstanceTypes?: boolean;
  enabled?: boolean;
};

export const useCommitmentQuery = ({
  commitmentId,
  includeUsage = true,
  withInstanceTypes = false,
  enabled = true,
}: UseCommitmentQueryArgs) => {
  const { isLoading, data, isError, error, refetch } = useQuery(
    getCommitmentQueryOptions({
      commitmentId,
      includeUsage,
      withInstanceTypes,
      enabled,
    })
  );

  return {
    data,
    isLoading,
    isError,
    error,
    refetch,
  };
};
